/**
 * Acquires both a reCAPTCHA Enterprise token and a Cloudflare Turnstile token.
 *
 * reCAPTCHA Enterprise is loaded globally via a <script> tag in the page
 * layout and exposes `grecaptcha.enterprise` on the window. No npm package
 * is used.
 *
 * The Turnstile widget is rendered with `appearance: 'interaction-only'` so it
 * is invisible unless a challenge is required. The widget is rendered once per
 * container and reused across submissions. On subsequent calls the widget is
 * reset so Turnstile fires its callback again; a new Promise is created for
 * each call so the fresh resolve/reject are always in scope when the callback
 * fires.
 *
 * @param {object} options
 * @param {string} options.recaptchaSiteKey  - reCAPTCHA Enterprise public site key
 * @param {string} options.turnstileSiteKey  - Cloudflare Turnstile public site key
 * @param {string} options.action            - Action label sent with both tokens (e.g. 'form', 'rebate').
 *                                             Must match the expectedAction checked server-side.
 * @param {HTMLElement} options.container    - DOM element to render the Turnstile widget inside
 *
 * @returns {Promise<{recaptchaToken: string, turnstileToken: string}>}
 */

/**
 * Internal registry that maps each container element to its Turnstile widget
 * state: the widget ID returned by `turnstile.render()` and mutable
 * resolve/reject/recaptchaToken references that are replaced on every call to
 * getSpamTokens.
 *
 * @type {WeakMap<HTMLElement, { widgetId: string|null, recaptchaToken: string|null, resolve: Function|null, reject: Function|null }>}
 */
const turnstileWidgets = new WeakMap();

export function getSpamTokens({
    recaptchaSiteKey,
    turnstileSiteKey,
    action,
    container,
}) {
    /* eslint-disable no-undef */
    if (typeof grecaptcha === 'undefined' || typeof grecaptcha.enterprise === 'undefined') {
        return Promise.reject(new Error('reCAPTCHA Enterprise is not loaded.'));
    }

    const recaptchaPromise = new Promise((resolve, reject) => {
        grecaptcha.enterprise.ready(() => {
            grecaptcha.enterprise.execute(recaptchaSiteKey, { action })
                .then(resolve)
                .catch(() => reject(new Error('reCAPTCHA Enterprise token acquisition failed.')));
        });
    });

    return recaptchaPromise.then(recaptchaToken => new Promise((resolve, reject) => {
        if (typeof turnstile === 'undefined') {
            reject(new Error('Cloudflare Turnstile is not loaded.'));

            return;
        }

        const existing = turnstileWidgets.get(container);

        if (existing) {
            // Swap in all three mutable values so the already-registered
            // callbacks always operate on the current submission's data.
            existing.recaptchaToken = recaptchaToken;
            existing.resolve = resolve;
            existing.reject = reject;
            turnstile.reset(existing.widgetId);
        } else {
            // First submission for this container: render the widget and store
            // mutable handler references in the registry.
            const state = { widgetId: null, recaptchaToken, resolve, reject };
            turnstileWidgets.set(container, state);

            turnstile.ready(() => {
                state.widgetId = turnstile.render(container, {
                    sitekey: turnstileSiteKey,
                    action,
                    appearance: 'interaction-only',
                    size: 'compact',
                    callback(turnstileToken) {
                        // Always reads current values from state — never from
                        // the stale closure of the original render() call.
                        state.resolve({ recaptchaToken: state.recaptchaToken, turnstileToken });
                    },
                    'error-callback': function () {
                        turnstile.reset(state.widgetId);
                        state.reject(new Error('Turnstile verification failed. Please try again.'));
                    },
                    'expired-callback': function () {
                        turnstile.reset(state.widgetId);
                        state.reject(new Error('Turnstile token expired. Please try again.'));
                    },
                });
            });
        }
    }));
    /* eslint-enable no-undef */
}
